import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { ButtonProps, LoadingButton } from '@eventbrite/marmalade';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import escape from 'lodash/escape';
import React, { useEffect, useState } from 'react';
import { LISTING_HEAP_IDS } from '../../../../constants';
import { useEventBasicInformationContext } from '../../../../contexts';
import { STATSIG_EVENT_NAMES } from '../../../../experimentation';
import {
    CheckoutWidgetProps,
    Event as EventProp,
    GACodes,
} from '../../../../types';
import { ConversionBarCommonProps } from '../ConversionBar/ConversionBar';
import { buildCheckoutUrl } from '../ConversionBar/utils';
import styles from './CheckoutModalButton.module.scss';

type CheckoutModalButtonProps = CheckoutWidgetProps &
    Pick<
        ConversionBarCommonProps,
        'shouldShowStatus' | 'ticketModalButton' | 'joinWaitlist' | 'isFree'
    > &
    Pick<EventProp, 'isPreview'> & {
        handleClick?: () => void;
        style?: ButtonProps['variant'];
    } & {
        isRemindMe?: boolean;
    };

declare global {
    interface Window {
        EBWidgets?: {
            createWidget: (params: $FixMe) => void;
        };
    }
}

export const CheckoutModalButton: React.FC<CheckoutModalButtonProps> = ({
    affiliateCode,
    campaign_token,
    children,
    handleClick,
    isPreview,
    promoCode,
    rsvpToken,
    shouldShowStatus,
    style,
    ticketModalButton,
    waitlistToken,
    joinWaitlist,
    isFree,
    isRemindMe,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const {
        id: eventId,
        isSeriesParent,
        name: eventName,
        serverUrl,
    } = useEventBasicInformationContext();
    const modalTriggerElementId = `eventbrite-widget-modal-trigger-${eventId}`;
    const freeCTAProperties = {
        text: gettext('Reserve a spot'),
        tracking_label: 'Register',
    };
    const trackingLabel = isSeriesParent
        ? GACodes.GA_LABEL_SERIES_PARENT
        : ticketModalButton?.tracking_label;

    const createCheckoutWidget = () => {
        const ebWidgets = getWindowObject('EBWidgets');
        if (!ebWidgets) {
            return false;
        }

        ebWidgets.createWidget({
            widgetType: 'checkout',
            eventId,
            modal: true,
            modalTriggerElementId,
            iframeTitle: gettext('Checkout for %(eventName)s', {
                eventName,
            }),
            ...(!isPreview ? { affiliateCode } : {}),
            ...(promoCode ? { promoCode: escape(promoCode) } : {}),
            ...(waitlistToken ? { waitlistToken } : {}),
            ...(rsvpToken ? { rsvpToken } : {}),
            ...(campaign_token ? { campaign_token } : {}),
        });

        return true;
    };

    useEffect(() => {
        // Try to create widget immediately
        const created = createCheckoutWidget();

        // If SDK not ready, retry after a delay (to avoid race conditions with Core Legacy Logic - EB-317602)
        if (!created) {
            const timeoutId = setTimeout(() => {
                createCheckoutWidget();
            }, 1000);

            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    useEffect(() => {
        setIsLoading(false); // Cancel loading behavior once the component hydrates
        // Auto-open modal if #tickets hash is present
        if (getWindowObject('location')?.hash === '#tickets') {
            document.getElementById(modalTriggerElementId)?.click();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buttonTrackingLabel =
        isFree && !isRemindMe
            ? freeCTAProperties.tracking_label
            : ticketModalButton?.tracking_label;
    const buttonText =
        isFree && !isRemindMe
            ? freeCTAProperties.text
            : ticketModalButton?.text;

    const handleClickWhenLoaded = (e: any) => {
        // Only prevent default if we're sure the SDK will handle it
        if (getWindowObject('EBWidgets')) {
            e.preventDefault();
            handleClick?.();
        }
    };

    // Users with the waitlist token should see the normal checkout button
    const shouldShowJoinWaitlistButton = joinWaitlist && !waitlistToken;

    // Having a link + button allow us to define a fallback behavior (open checkout-external) under low connection / No JS available scenarios (EB-315757)
    // Once the checkout modal is ready, we won't do the fallback behavior
    return (
        <a
            target="_blank"
            href={buildCheckoutUrl(
                serverUrl,
                eventId,
                promoCode,
                waitlistToken,
                campaign_token,
            )}
            rel="noopener noreferrer"
            onClick={!isLoading ? handleClickWhenLoaded : undefined}
        >
            <LoadingButton
                isLoading={isLoading}
                disabled={isPreview}
                id={modalTriggerElementId}
                data-testid="checkout-link"
                variant={style ?? (shouldShowStatus ? 'secondary' : 'primary')}
                data-tracking-label={buttonTrackingLabel}
                className={classNames(
                    {
                        'js-embed-ticket-modal-btn': !isSeriesParent,
                    },
                    styles.checkoutModalButton,
                )}
                size="responsive"
                onClick={() => {
                    logEvent(
                        STATSIG_EVENT_NAMES.LISTING_CONVERSION_BAR_CTA_CLICK,
                    );
                    trackAnalyticsEvent({
                        action: GACodes.GA_ACTION_CHECKOUT,
                        category: GACodes.GA_CATEGORY_LISTING,
                        label: trackingLabel,
                    });
                    handleClick?.();
                }}
                data-heap-id={LISTING_HEAP_IDS.CHECKOUT_BUTTON}
            >
                {shouldShowJoinWaitlistButton
                    ? gettext('Join waitlist')
                    : children ?? buttonText}
            </LoadingButton>
        </a>
    );
};
